import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useGame } from "../contexts/GameContext";
import { useProfile } from "../contexts/ProfileContext";
import { CLASSES, ENERGY_MAX, BERGER_MUTTONS_INIT, BERGER_MUTTONS_MAX } from "../data/classes";
import {
  computeDamageWithFormula,
  executeCompetence,
  tickStatusEffects,
  mergeStatus,
  newStatusEffects,
  effectiveStats,
  computeBuffsValues,
  isFrozen,
  goldAtkBonus,
  goldUltBonus,
  goldStealAmount,
  rollStatusResist,
  ELEMENTS,
  elementMultiplier,
} from "../utils/gameLogic";
import { newEnemyAIState, tickEnemyCooldowns, chooseEnemyAbility } from "../utils/enemyAI";
import { HealthBar } from "../components/game/StatsDisplay";
import { EnemySprite } from "../components/game/EnemySprite";
import { EnemyAbilities } from "../components/game/EnemyAbilities";
import { enemySpriteUrl, enemyFlipToFacePlayer } from "../utils/enemySprites";
import { themeForFloor } from "../utils/themes";
import { Shield, Trophy, Backpack, Gauge, Play, Pause } from "lucide-react";
import { SFX, playMusic, trackForFloor } from "../utils/audio";
import { InventoryModal } from "../components/game/InventoryModal";
import { BreathingSprite } from "../components/game/BreathingSprite";
import { AttackFX } from "../components/game/AttackFX";
import { ITEMS } from "../data/items";
import { removeItemFromInventory } from "../utils/gameLogic";

const FloatingDamage = ({ value, isCrit, x, y, color, prefix, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 1000);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div
      className="pointer-events-none fixed font-display font-bold animate-damage-pop z-30"
      style={{
        left: x, top: y,
        color: color || (isCrit ? "#fbbf24" : "#f87171"),
        fontSize: isCrit ? "2.5rem" : "1.75rem",
        textShadow: "2px 2px 0 #000, -2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000",
      }}
    >
      {prefix}{isCrit && "✨ "}{value}{isCrit && " ✨"}
    </div>
  );
};

const StatusBadges = ({ effects, testId }) => {
  const items = [];
  if (effects.brulure?.duration > 0) items.push({ icon: "🔥", label: `Brûlure ${effects.brulure.damage} (${effects.brulure.duration})`, color: "bg-rose-600" });
  if (effects.gel?.duration > 0) items.push({ icon: "❄️", label: `Gel (${effects.gel.duration})`, color: "bg-cyan-500" });
  if (effects.stun?.duration > 0) items.push({ icon: "⭐", label: `Stun (${effects.stun.duration})`, color: "bg-yellow-500" });
  if (effects.garde?.duration > 0) items.push({ icon: "🛡️", label: `Garde (${effects.garde.duration})`, color: "bg-blue-500" });
  if (effects.posture?.duration > 0) items.push({ icon: "🦅", label: `Posture armée`, color: "bg-orange-500" });
  if (effects.provoque?.duration > 0) items.push({ icon: "📣", label: `Provoqué (${effects.provoque.duration})`, color: "bg-pink-500" });
  const buffs = effects.combatBuffs || {};
  const bnsParts = [];
  if (buffs.atk) bnsParts.push(`ATK+${buffs.atk}`);
  if (buffs.def) bnsParts.push(`DEF+${buffs.def}`);
  if (buffs.vit) bnsParts.push(`VIT+${buffs.vit}`);
  if (buffs.crit) bnsParts.push(`TC+${buffs.crit}%`);
  if (buffs.pv) bnsParts.push(`PV+${buffs.pv}`);
  if (buffs.res) bnsParts.push(`RÉS+${buffs.res}%`);
  if (bnsParts.length) items.push({ icon: "✨", label: bnsParts.join(" "), color: "bg-purple-500" });
  const timed = effects.timedBuffs || [];
  if (timed.length) items.push({ icon: "⏳", label: timed.map((t) => `${(t.stat || "").toUpperCase()}(${t.duration})`).join(" "), color: "bg-indigo-500" });
  if (items.length === 0) return null;
  return (
    <div data-testid={testId} className="flex flex-wrap gap-1 mt-2 justify-center">
      {items.map((it, i) => (
        <span key={i} className={`${it.color} text-white border-2 border-slate-950 rounded-md px-1.5 py-0.5 text-xs font-bold flex items-center gap-1`}>
          {it.icon} {it.label}
        </span>
      ))}
    </div>
  );
};

const Combat = () => {
  const { character, currentEncounter, updateCharacter, completeEncounter, floor, bumpMeta } = useGame();
  const { unlockAchievement, profile } = useProfile();
  const navigate = useNavigate();

  const [enemyHp, setEnemyHp] = useState(currentEncounter?.enemy?.pvCurrent || 0);
  const [enemyEffects, setEnemyEffects] = useState(newStatusEffects());
  const [playerEffects, setPlayerEffects] = useState(newStatusEffects());
  const [log, setLog] = useState(["Combat commencé !"]);
  const [playerTurn, setPlayerTurn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [floatingDmg, setFloatingDmg] = useState([]);
  const [shakingEnemy, setShakingEnemy] = useState(false);
  const [shakingPlayer, setShakingPlayer] = useState(false);
  // Animations d'attaque (style Summoners War) : lunge + effet visuel sur la cible
  const [playerAttacking, setPlayerAttacking] = useState(false);
  const [enemyAttacking, setEnemyAttacking] = useState(false);
  const [attackFx, setAttackFx] = useState(null); // { id, type, target }
  // Impact "2.5D" : secousse d'écran + flash plein écran sur coups critiques / ultimes
  const [screenShake, setScreenShake] = useState(false);
  const [critFlash, setCritFlash] = useState(false);
  const [energy, setEnergy] = useState(character?.energy ?? 3);
  const [victoryShown, setVictoryShown] = useState(false);
  const [muttons, setMuttons] = useState(character?.classeId === "berger" ? BERGER_MUTTONS_INIT : 0);
  const [combatInvOpen, setCombatInvOpen] = useState(false);
  // Bâtisseur : or volé cumulé pendant ce combat (affichage HUD)
  const [goldStolen, setGoldStolen] = useState(0);
  // Mode "Summoners War" : auto-combat (l'IA joue tes tours) + vitesse (x1/x2/x3)
  const [autoMode, setAutoMode] = useState(false);
  const [combatSpeed, setCombatSpeed] = useState(1);
  // Boss signature : état de charge télégraphiée (null = aucune charge en cours)
  const [charging, setCharging] = useState(null);
  // Vue des cooldowns de l'ennemi (miroir de enemyAIRef pour l'affichage des compétences)
  const [enemyCdView, setEnemyCdView] = useState({});

  // Handler pour utiliser un item DURANT le combat (bomb, smoke_bomb, et tout autre)
  const handleUseInCombat = useCallback((entry, item) => {
    if (!playerTurn || busy) return false;
    const eff = item.effect;
    if (eff.type === "direct_damage") {
      // La bombe scale avec l'étage de la run (avant : 40 fixe quel que soit l'étage)
      const scaled = Math.round(eff.value * (0.6 + 0.4 * (floor || 1)));
      const before = enemyHpRef.current;
      const newHp = Math.max(0, before - scaled);
      setEnemyHp(newHp);
      enemyHpRef.current = newHp;
      addLog(`${item.icon} ${item.nom} : -${scaled} PV à l'ennemi.`);
      updateCharacter((c) => removeItemFromInventory(c, entry.uuid));
      // Peut interrompre une charge de boss si les dégâts atteignent le seuil
      if (chargingRef.current && scaled >= Math.max(1, Math.round(enemyMaxHpRef.current * 0.10))) {
        addLog(`💥 Charge INTERROMPUE ! ${chargingRef.current.nom} annulé !`);
        chargingRef.current = null;
        chargeBlockedRef.current = false;
        setCharging(null);
      }
      if (newHp <= 0) {
        // Trigger victory flow on next tick
        setTimeout(() => handleVictory?.(), 50);
      }
      return true;
    }
    if (eff.type === "flee_combat") {
      addLog(`${item.icon} Tu disparais dans la fumée !`);
      updateCharacter((c) => removeItemFromInventory(c, entry.uuid));
      setTimeout(() => completeEncounter({ gold: 0, noDamageTaken: combatTotalsRef.current.taken === 0 }), 800);
      return true;
    }
    if (eff.type === "heal_percent") {
      updateCharacter((c) => {
        if (!c) return c;
        const heal = Math.ceil(c.stats.pvMax * eff.value);
        return removeItemFromInventory({ ...c, stats: { ...c.stats, pv: Math.min(c.stats.pvMax, c.stats.pv + heal) } }, entry.uuid);
      });
      addLog(`${item.icon} ${item.nom} : Tu te soignes.`);
      bumpMeta?.("totalPotions", 1, { 30: "potion_addict" });
      return true;
    }
    if (eff.type === "permanent_stat") {
      updateCharacter((c) => {
        if (!c) return c;
        const updated = { ...c, stats: { ...c.stats } };
        updated.stats[eff.stat] = (updated.stats[eff.stat] || 0) + eff.value;
        if (eff.stat === "pv") updated.stats.pvMax = (updated.stats.pvMax || 0) + eff.value;
        return removeItemFromInventory(updated, entry.uuid);
      });
      addLog(`${item.icon} ${item.nom} appliqué !`);
      return true;
    }
    if (eff.type === "multi_stat") {
      updateCharacter((c) => {
        if (!c) return c;
        const updated = { ...c, stats: { ...c.stats } };
        for (const [stat, val] of Object.entries(eff.stats || {})) {
          updated.stats[stat] = (updated.stats[stat] || 0) + val;
          if (stat === "pv") updated.stats.pvMax = Math.max(1, (updated.stats.pvMax || 0) + val);
        }
        if (updated.stats.pv > updated.stats.pvMax) updated.stats.pv = updated.stats.pvMax;
        return removeItemFromInventory(updated, entry.uuid);
      });
      addLog(`${item.icon} ${item.nom} appliqué !`);
      return true;
    }
    if (eff.type === "all_flat_stats") {
      updateCharacter((c) => {
        if (!c) return c;
        const updated = { ...c, stats: { ...c.stats } };
        for (const s of ["atk", "def", "vit", "pv", "crit", "degatsCrit", "degatsElem", "res", "precision"]) {
          updated.stats[s] = (updated.stats[s] || 0) + eff.value;
          if (s === "pv") updated.stats.pvMax = (updated.stats.pvMax || 0) + eff.value;
        }
        return removeItemFromInventory(updated, entry.uuid);
      });
      addLog(`${item.icon} ${item.nom} : +${eff.value} à toutes les stats !`);
      return true;
    }
    if (eff.type === "percent_all_stats") {
      updateCharacter((c) => {
        if (!c) return c;
        const updated = { ...c, stats: { ...c.stats } };
        for (const s of ["atk", "def", "vit", "pv", "crit", "degatsCrit", "degatsElem", "res", "precision"]) {
          const add = Math.round((updated.stats[s] || 0) * eff.value);
          updated.stats[s] = (updated.stats[s] || 0) + add;
          if (s === "pv") updated.stats.pvMax = (updated.stats.pvMax || 0) + add;
        }
        return removeItemFromInventory(updated, entry.uuid);
      });
      addLog(`${item.icon} ${item.nom} : +${Math.round(eff.value * 100)}% à toutes les stats !`);
      return true;
    }
    if (eff.type === "percent_stat") {
      updateCharacter((c) => {
        if (!c) return c;
        const updated = { ...c, stats: { ...c.stats } };
        const add = Math.round((updated.stats[eff.stat] || 0) * eff.value);
        updated.stats[eff.stat] = (updated.stats[eff.stat] || 0) + add;
        if (eff.stat === "pv") updated.stats.pvMax = (updated.stats.pvMax || 0) + add;
        return removeItemFromInventory(updated, entry.uuid);
      });
      addLog(`${item.icon} ${item.nom} : +${Math.round(eff.value * 100)}% ${eff.stat.toUpperCase()} !`);
      return true;
    }
    if (eff.type === "revive_token") {
      updateCharacter((c) => removeItemFromInventory({ ...c, reviveTokens: (c.reviveTokens || 0) + 1 }, entry.uuid));
      addLog(`${item.icon} ${item.nom} : tu seras ressuscité à ta prochaine mort.`);
      return true;
    }
    if (eff.type === "refill_energy") {
      setEnergy(CLASSES[character.classeId]?.energyMax || ENERGY_MAX);
      updateCharacter((c) => removeItemFromInventory(c, entry.uuid));
      addLog(`${item.icon} Énergie restaurée au maximum !`);
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerTurn, busy, updateCharacter, completeEncounter, bumpMeta]);
  // Per-combat totals for the victory recap
  const [combatTotals, setCombatTotals] = useState({ dealt: 0, taken: 0, maxHit: 0, crits: 0 });
  const combatTotalsRef = useRef({ dealt: 0, taken: 0, maxHit: 0, crits: 0 });
  const enemyAreaRef = useRef(null);
  const playerAreaRef = useRef(null);

  // Refs synchronisées (toujours à jour)
  const playerEffectsRef = useRef(playerEffects);
  const enemyEffectsRef = useRef(enemyEffects);
  const enemyHpRef = useRef(enemyHp);
  const energyRef = useRef(energy);
  // Vitesse de combat : divise tous les délais d'animation. Le shadow local de setTimeout
  // (ci-dessous) applique automatiquement la vitesse à TOUS les setTimeout du composant.
  const speedRef = useRef(1);
  useEffect(() => { speedRef.current = combatSpeed; }, [combatSpeed]);
  // eslint-disable-next-line no-shadow-restricted-names
  const setTimeout = (fn, ms = 0) => window.setTimeout(fn, Math.max(0, ms) / Math.max(0.5, speedRef.current || 1));

  // Auto-combat : quand activé, l'IA joue automatiquement le tour du joueur.
  useEffect(() => {
    if (!autoMode || !playerTurn || busy || victoryShown || enemyHp <= 0) return;
    const t = window.setTimeout(() => {
      const cls = CLASSES[character?.classeId];
      if (!cls) return;
      const comps = cls.competences.filter((c) => {
        const unlocked = c.purchasable ? (character.purchasedCompetences || []).includes(c.id) : (c.unlockLevel || 1) <= (character.runLevel || 1);
        const muttonOk = !c.requireMuttons || muttons >= c.requireMuttons;
        return unlocked && energy >= c.cout && muttonOk;
      });
      if (!comps.length) { autoDefendRef.current?.(); return; }
      const ult = comps.find((c) => c.ultimate && c.type === "attaque");
      const atk = comps.filter((c) => c.type === "attaque").sort((a, b) => (b.cout || 0) - (a.cout || 0));
      const choice = ult || atk[0] || comps[0];
      autoSkillRef.current?.(choice);
    }, 650 / (speedRef.current || 1));
    return () => window.clearTimeout(t);
  }, [autoMode, playerTurn, busy, victoryShown, enemyHp, energy, muttons, character]);
  // Réfs vers les handlers (définis plus bas) pour que l'effet auto utilise toujours la dernière version
  const autoSkillRef = useRef(null);
  const autoDefendRef = useRef(null);
  const muttonsRef = useRef(muttons);
  const endedRef = useRef(false);
  // IA ennemie : cooldowns de compétences + compteur de tours, et PV max de l'ennemi (pour les soins)
  const enemyAIRef = useRef(newEnemyAIState());
  const enemyMaxHpRef = useRef(currentEncounter?.enemy?.pvCurrent || currentEncounter?.enemy?.stats?.pv || 1);
  // Boss signature : charge en cours (ref pour la logique) + blocage (Défendre pendant la charge)
  const chargingRef = useRef(null);
  const chargeBlockedRef = useRef(false);
  // ===== Suivi des succès (par combat) =====
  const attackUsedRef = useRef(false);      // une compétence de type "attaque" a été utilisée
  const nonBaseUsedRef = useRef(false);     // une compétence autre que "base" a été utilisée
  const freezeAppliedRef = useRef(0);       // nb de gels posés sur l'ennemi ce combat
  const turnNumberRef = useRef(1);          // numéro du tour joueur courant
  const lowestPvRef = useRef(Infinity);     // PV le plus bas atteint ce combat
  const finalPvRef = useRef(null);          // PV à la victoire (capturés dans handleVictory)
  const finalPvMaxRef = useRef(null);
  const turnStartRef = useRef(Date.now());  // début du tour courant (overthinker)
  useEffect(() => { if (playerTurn) turnStartRef.current = Date.now(); }, [playerTurn]);
  useEffect(() => { playerEffectsRef.current = playerEffects; }, [playerEffects]);
  useEffect(() => { enemyEffectsRef.current = enemyEffects; }, [enemyEffects]);
  useEffect(() => { enemyHpRef.current = enemyHp; }, [enemyHp]);
  useEffect(() => { energyRef.current = energy; }, [energy]);
  useEffect(() => { muttonsRef.current = muttons; }, [muttons]);

  // ============ VICTOIRE ============
  const handleVictory = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    setVictoryShown(true);
    SFX.victory();
    const enemy = currentEncounter?.enemy;
    if (!enemy) return;
    const goldReward = enemy.goldReward || 10;
    const isBoss = !!enemy.isBoss;
    updateCharacter((c) => {
      if (!c) return c;
      const rs = { ...(c.runStats || {}) };
      rs.kills = (rs.kills || 0) + 1;
      rs.enemiesDefeated = (rs.enemiesDefeated || 0) + (isBoss ? 0 : 1);
      rs.bossesDefeated = (rs.bossesDefeated || 0) + (isBoss ? 1 : 0);
      rs.goldEarned = (rs.goldEarned || 0) + goldReward;
      finalPvRef.current = c.stats.pv;
      finalPvMaxRef.current = c.stats.pvMax;
      return { ...c, energy: Math.min(CLASSES[c.classeId]?.energyMax || ENERGY_MAX, energyRef.current + 1), runStats: rs };
    });
    completeEncounter({ gold: goldReward, noDamageTaken: (combatTotalsRef.current?.taken || 0) === 0, damageDealt: combatTotalsRef.current?.dealt || 0 });
    // === Succès de combat (vérifiés juste après la mise à jour du personnage) ===
    setTimeout(() => {
      const taken = combatTotalsRef.current?.taken || 0;
      const pvMax = finalPvMaxRef.current || character?.stats?.pvMax || 1;
      const finalPv = finalPvRef.current;
      const lowPct = (lowestPvRef.current === Infinity ? pvMax : lowestPvRef.current) / Math.max(1, pvMax) * 100;
      if (taken === 0) {
        unlockAchievement?.("perfect_combat");
        if (isBoss) unlockAchievement?.("no_hit_boss");
      }
      if (taken >= 500) unlockAchievement?.("tank_run");
      if (finalPv === 1) unlockAchievement?.("one_hp_left");
      if (lowPct <= 5) unlockAchievement?.("comeback_kid");
      if (lowPct <= 10) unlockAchievement?.("last_breath");
      if (!attackUsedRef.current) unlockAchievement?.("pacifist");
      if (!nonBaseUsedRef.current) unlockAchievement?.("minimalist");
      // "Le sol est de la lave" : gagner un combat (on ne touche aucune porte en combat)
      unlockAchievement?.("the_floor_is");
    }, 80);
    setTimeout(() => SFX.coin(), 400);
    setTimeout(() => navigate("/map"), 3200);
  }, [currentEncounter, completeEncounter, updateCharacter, navigate, unlockAchievement, character]);

  // ============ INIT ============
  useEffect(() => {
    if (!character || !currentEncounter) {
      navigate("/map");
      return;
    }
    // Choose track: boss > pvp-style theme > regular combat per floor element
    playMusic(currentEncounter.enemy.isBoss ? "boss" : trackForFloor(floor || 1));
    // Succès : init suivi PV bas + "YOLO" (affronter un boss à < 20% PV)
    lowestPvRef.current = character.stats.pv;
    const pvFrac = character.stats.pv / Math.max(1, character.stats.pvMax || character.stats.pv);
    if (currentEncounter.enemy.isBoss && pvFrac < 0.2) unlockAchievement?.("yolo");
    const playerSpeed = character.stats.vit;
    const enemySpeed = currentEncounter.enemy.stats.vit;
    const playerFirst = playerSpeed >= enemySpeed;
    setPlayerTurn(playerFirst);
    if (!playerFirst) {
      setTimeout(() => enemyTurn(), 800);
    }
    // eslint-disable-next-line
  }, []);

  if (!character || !currentEncounter) return null;
  const enemy = currentEncounter.enemy;
  const classe = CLASSES[character.classeId];
  const energyCap = classe?.energyMax || ENERGY_MAX;

  // Helpers
  const playerEffective = () => {
    const eff = effectiveStats(character.stats, playerEffectsRef.current.combatBuffs || {});
    // Bâtisseur : l'or amassé dans la run booste l'ATK effective
    if (classe?.scalesWithGold) {
      eff.atk = (eff.atk || 0) + goldAtkBonus(character.runStats?.goldEarned || 0);
    }
    return eff;
  };
  const enemyEffective = () => effectiveStats(enemy.stats, enemyEffectsRef.current.combatBuffs || {});

  const addLog = (text) => setLog((l) => [...l.slice(-6), text]);
  const addLogs = (texts) => setLog((l) => [...l.slice(-6), ...texts].slice(-7));

  const showFloatingDmg = (val, isCrit, target, prefix = "") => {
    const el = target === "enemy" ? enemyAreaRef.current : playerAreaRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const id = Date.now() + Math.random();
    setFloatingDmg((arr) => [...arr, {
      id, value: val, isCrit, prefix,
      x: rect.left + rect.width / 2 - 40 + (Math.random() * 40 - 20),
      y: rect.top + 40,
    }]);
  };

  const removeFloating = (id) => setFloatingDmg((arr) => arr.filter((f) => f.id !== id));

  // Mappe un élément de jeu vers un type d'effet visuel d'attaque
  const elementFx = (element) => {
    switch (element) {
      case "feu": return "fire";
      case "eau": return "ice";
      case "terre": return "earth";
      case "air": return "wind";
      case "lumiere": return "light";
      case "dark": return "shadow";
      default: return "slash";
    }
  };

  // Déclenche une animation d'attaque : l'attaquant bondit vers la cible + effet visuel sur la cible.
  // Utilise le setTimeout "shadowé" (mis à l'échelle par la vitesse de combat).
  const playAttack = (attacker, target, fxType) => {
    if (attacker === "player") {
      setPlayerAttacking(true);
      setTimeout(() => setPlayerAttacking(false), 360);
    } else {
      setEnemyAttacking(true);
      setTimeout(() => setEnemyAttacking(false), 360);
    }
    const id = Date.now() + Math.random();
    setAttackFx({ id, type: fxType, target });
    setTimeout(() => setAttackFx((f) => (f && f.id === id ? null : f)), 720);
  };

  // Impact fort (crit / ultime) : secousse de l'arène + flash blanc bref
  const triggerCritImpact = () => {
    setScreenShake(true);
    setTimeout(() => setScreenShake(false), 430);
    setCritFlash(true);
    setTimeout(() => setCritFlash(false), 270);
  };

  // Applique des combatBuffs sur un set d'effects
  const applyCombatBuffs = (effects, buffValues) => {
    const newEffects = { ...effects, combatBuffs: { ...(effects.combatBuffs || {}) } };
    for (const [k, v] of Object.entries(buffValues || {})) {
      newEffects.combatBuffs[k] = (newEffects.combatBuffs[k] || 0) + v;
    }
    return newEffects;
  };

  // Plume de Phénix : intercepte la mort en combat → renaissance à 50% PV (consomme 1 token)
  const tryRevive = () => {
    if ((character.reviveTokens || 0) <= 0) return false;
    const pvMax = character.stats.pvMax || character.stats.pv || 1;
    const revivePv = Math.max(1, Math.ceil(pvMax * 0.5));
    updateCharacter((c) => {
      if (!c) return c;
      return { ...c, reviveTokens: (c.reviveTokens || 1) - 1, stats: { ...c.stats, pv: revivePv } };
    });
    addLog("🪶 Plume de Phénix consommée ! Tu renais à 50% PV !");
    SFX.heal?.();
    return true;
  };

  // Applique les effets d'une compétence ENNEMIE sur le JOUEUR (brûlure / stun / debuff de stat).
  // Soumis à un jet de résistance (RÉS du joueur). Retourne true si au moins un effet a été posé.
  // NB: stun/gel reçoivent +1 de durée car les effets joueur sont décrémentés à la fin du tour ennemi
  // (juste après l'action ennemie) — sans ce +1, un stun(1) disparaîtrait avant le tour du joueur.
  const applyEnemyEffectsOnPlayer = (cfg, logs) => {
    if (!cfg) return false;
    if (!rollStatusResist(playerEffective())) {
      logs.push(`🛡️ Vous résistez à l'effet néfaste (RÉS ${character.stats.res || 0}%) !`);
      return false;
    }
    let pe = playerEffectsRef.current;
    const incoming = {};
    if (cfg.brulure && enemy.element === "feu") {
      const dmg = cfg.brulure.useElem ? Math.max(1, enemy.stats.degatsElem || 1) : (cfg.brulure.damage || 1);
      incoming.brulure = { duration: cfg.brulure.duration, damage: Math.max(1, dmg) };
      logs.push(`🔥 Vous êtes brûlé (${cfg.brulure.duration} tour${cfg.brulure.duration > 1 ? "s" : ""}) !`);
    }
    if (cfg.stun) {
      incoming.stun = { duration: (cfg.stun.duration || 1) + 1 };
      logs.push("⭐ Vous êtes étourdi !");
    }
    if (cfg.gel && enemy.element === "eau") {
      incoming.gel = { duration: (cfg.gel.duration || 1) + 1 };
      logs.push("❄️ Vous êtes gelé !");
    }
    if (Object.keys(incoming).length) pe = mergeStatus(pe, incoming);
    if (cfg.combatBuff) {
      const arr = Array.isArray(cfg.combatBuff) ? cfg.combatBuff : [cfg.combatBuff];
      const buffVals = {};
      for (const b of arr) buffVals[b.stat] = (buffVals[b.stat] || 0) + (b.value || 0);
      pe = applyCombatBuffs(pe, buffVals);
      const parts = Object.entries(buffVals).map(([k, v]) => `${v > 0 ? "+" : ""}${v} ${k.toUpperCase()}`).join(", ");
      if (parts) logs.push(`🔻 ${parts}`);
    }
    setPlayerEffects(pe);
    playerEffectsRef.current = pe;
    return true;
  };

  // ============ JOUEUR : EXÉCUTE UNE COMPÉTENCE ============
  const handleSkill = (competence) => {
    if (!playerTurn || busy) return;
    // Compétence verrouillée
    const isPurchased = (character.purchasedCompetences || []).includes(competence.id);
    if (competence.purchasable && !isPurchased) {
      addLog(`💰 ${competence.nom} — Achète-la dans une boutique (${competence.purchaseCost} or).`);
      SFX.miss();
      return;
    }
    if (!competence.purchasable && (competence.unlockLevel || 1) > (character.runLevel || 1)) {
      addLog(`🔒 ${competence.nom} — Niv. ${competence.unlockLevel} requis.`);
      SFX.miss();
      return;
    }
    if (energy < competence.cout) return;
    // Cas spécial : Berger sans mouton
    if (competence.requireMuttons && muttonsRef.current < competence.requireMuttons) {
      addLog(`🐑 Pas assez de moutons (${muttonsRef.current}/${competence.requireMuttons}).`);
      SFX.miss();
      return;
    }
    SFX.click();
    // Stun: skip turn
    if (playerEffectsRef.current.stun?.duration > 0) {
      addLog("Vous êtes ÉTOURDI ! Vous passez votre tour.");
      setBusy(true);
      setTimeout(() => endPlayerTurn(), 800);
      return;
    }
    setBusy(true);

    // Succès : suivi des compétences utilisées
    if (competence.type === "attaque") attackUsedRef.current = true;
    if (competence.id !== "base") nonBaseUsedRef.current = true;
    bumpMeta?.("skillClicks", 1, { 500: "button_masher" });
    if (Date.now() - turnStartRef.current > 60 * 1000) unlockAchievement?.("overthinker");

    // Consomme énergie
    const newEnergy = energy - competence.cout;
    setEnergy(newEnergy);

    // Calcul des "bonus" pour formules (PV_Bns, DEF_Bns, ATK_Bns, VIT_Bns, muttonsConsumed)
    const buffs = playerEffectsRef.current.combatBuffs || {};
    let muttonsConsumed = 0;
    if (competence.consumeAllMuttons) {
      muttonsConsumed = Math.min(muttonsRef.current, 5);
    } else if (competence.consumeMuttons) {
      muttonsConsumed = competence.consumeMuttons;
    }
    // Le Berger garde TOUJOURS au moins 1 mouton (jamais en dessous de 1)
    muttonsConsumed = Math.min(muttonsConsumed, Math.max(0, muttonsRef.current - 1));
    const extras = {
      pvBns: buffs.pv || 0,
      defBns: buffs.def || 0,
      atkBns: buffs.atk || 0,
      vitBns: buffs.vit || 0,
      muttonsConsumed,
      goldPower: goldUltBonus(character.runStats?.goldEarned || 0),
      enemyEffects: enemyEffectsRef.current,
      atkElement: classe.element,
      defElement: enemy.element,
    };

    // Compute attaque
    const playerStats = playerEffective();
    const enemyStats = enemyEffective();
    const result = executeCompetence(competence, playerStats, enemyStats, extras);

    // Animation d'attaque (style Summoners War) selon la compétence
    if (competence.type === "attaque") {
      playAttack("player", "enemy", elementFx(competence.element || classe.element));
    } else {
      playAttack("player", "player", "buff");
    }

    const logs = [`✨ ${competence.nom}`];
    let curEnemyHp = enemyHpRef.current;
    let curEnemyEffects = enemyEffectsRef.current;
    let curPlayerEffects = playerEffectsRef.current;
    let executed = false;

    // Cas spécial Monde Ténébreux: exécution si VIT ennemi <= VIT_Bns
    if (result.executeIfVitLower) {
      if ((enemy.stats.vit || 0) <= (extras.vitBns || 0)) {
        executed = true;
        curEnemyHp = 0;
        showFloatingDmg("EXÉCUTION", true, "enemy", "🕳️ ");
        logs.push("💀 EXÉCUTION ! L'ennemi est exécuté instantanément !");
      }
    }

    // Apply hits
    let damageThisAction = 0;
    let critsThisAction = 0;
    if (!executed) {
      for (const hit of result.hits) {
        if (hit.hit) {
          curEnemyHp = Math.max(0, curEnemyHp - hit.damage);
          damageThisAction += hit.damage;
          if (hit.crit) critsThisAction += 1;
          showFloatingDmg(hit.damage, hit.crit, "enemy");
          logs.push(`${hit.damage} dégâts${hit.crit ? " (CRITIQUE)" : ""}`);
          setShakingEnemy(true);
          setTimeout(() => setShakingEnemy(false), 400);
        } else {
          logs.push("Esquive !");
        }
      }
    } else {
      // Execution counts as kill but huge damage (best-effort: track remaining HP as dmg)
      damageThisAction += enemyHpRef.current;
    }
    if (damageThisAction > 0 || critsThisAction > 0) {
      const t = combatTotalsRef.current;
      t.dealt += damageThisAction;
      t.crits += critsThisAction;
      t.maxHit = Math.max(t.maxHit, damageThisAction);
      setCombatTotals({ ...t });
      // Impact "2.5D" : secousse + flash sur critique ou compétence ultime
      if (critsThisAction > 0 || competence.ultimate) triggerCritImpact();
      // Bâtisseur : vole de l'or à l'ennemi à chaque attaque qui touche.
      // Montant ajusté selon l'étage et le niveau du joueur.
      let stolen = 0;
      if (classe?.stealsGold && competence.type === "attaque" && damageThisAction > 0) {
        stolen = goldStealAmount(floor || 1, profile?.level || character?.runLevel || 1);
        showFloatingDmg(`+${stolen}🪙`, false, "enemy", "💰 ");
        logs.push(`💰 Or volé : +${stolen} 🪙`);
        setGoldStolen((g) => g + stolen);
      }
      updateCharacter((c) => {
        if (!c) return c;
        const rs = { ...(c.runStats || {}), damageBySkill: { ...(c.runStats?.damageBySkill || {}) } };
        rs.totalDamageDealt = (rs.totalDamageDealt || 0) + damageThisAction;
        rs.maxHit = Math.max(rs.maxHit || 0, damageThisAction);
        rs.criticalsLanded = (rs.criticalsLanded || 0) + critsThisAction;
        rs.turnsPlayed = (rs.turnsPlayed || 0) + 1;
        rs.damageBySkill[competence.nom] = (rs.damageBySkill[competence.nom] || 0) + damageThisAction;
        // L'or volé alimente la cagnotte ET le scaling d'ATK (goldEarned) du Bâtisseur
        if (stolen > 0) rs.goldEarned = (rs.goldEarned || 0) + stolen;
        return { ...c, gold: (c.gold || 0) + stolen, runStats: rs };
      });
      // Succès : coup critique au tout premier tour
      if (critsThisAction > 0 && turnNumberRef.current === 1) unlockAchievement?.("lucky_one");
      // Succès : 300+ dégâts avec l'ultime du Berger "Terre des Ancêtres"
      if (competence.id === "terre_ancetres" && damageThisAction >= 300) unlockAchievement?.("berger_supremacy");
    }

    // Détonation Embrasée : fait exploser toutes les brûlures de l'ennemi (× multiplicateur des dégâts restants)
    if (competence.detonateBurn) {
      if (curEnemyEffects.brulure && curEnemyEffects.brulure.duration > 0) {
        const b = curEnemyEffects.brulure;
        const remaining = (b.duration || 0) * (b.damage || 0);
        const boom = Math.max(0, Math.round(remaining * competence.detonateBurn));
        if (boom > 0) {
          curEnemyHp = Math.max(0, curEnemyHp - boom);
          damageThisAction += boom;
          showFloatingDmg(boom, true, "enemy", "💥 ");
          logs.push(`💥 Détonation des brûlures : ${boom} dégâts (${competence.detonateBurn}× ${remaining} restants) !`);
          setShakingEnemy(true);
          setTimeout(() => setShakingEnemy(false), 400);
          SFX.crit();
          const t = combatTotalsRef.current;
          t.dealt += boom;
          t.maxHit = Math.max(t.maxHit, boom);
          setCombatTotals({ ...t });
          updateCharacter((c) => {
            if (!c) return c;
            const rs = { ...(c.runStats || {}) };
            rs.totalDamageDealt = (rs.totalDamageDealt || 0) + boom;
            rs.maxHit = Math.max(rs.maxHit || 0, boom);
            return { ...c, runStats: rs };
          });
        }
        curEnemyEffects = { ...curEnemyEffects, brulure: null };
      } else {
        logs.push("💨 Aucune brûlure à faire exploser !");
      }
    }

    // BOSS : interruption d'une charge signature si l'action inflige >= 10% des PV max du boss
    if (chargingRef.current) {
      const threshold = Math.max(1, Math.round(enemyMaxHpRef.current * 0.10));
      if (damageThisAction >= threshold) {
        logs.push(`💥 Charge INTERROMPUE ! ${chargingRef.current.nom} annulé !`);
        chargingRef.current = null;
        chargeBlockedRef.current = false;
        setCharging(null);
        SFX.crit?.();
      } else {
        logs.push(`⚠️ Pas assez de dégâts pour interrompre (${damageThisAction}/${threshold}).`);
      }
    }

    // Apply onEnemy statuses & buffs
    if (result.applyOnEnemy) {
      curEnemyEffects = mergeStatus(curEnemyEffects, result.applyOnEnemy);
      if (result.applyOnEnemy._buffsToApply) {
        const enemyStatsRef = effectiveStats(enemy.stats, curEnemyEffects.combatBuffs || {});
        const buffVals = computeBuffsValues(result.applyOnEnemy._buffsToApply, enemyStatsRef, curEnemyEffects.combatBuffs || {});
        curEnemyEffects = applyCombatBuffs(curEnemyEffects, buffVals);
      }
      if (result.applyOnEnemy.brulure) logs.push(`🔥 Brûlure appliquée`);
      if (result.applyOnEnemy.gel) {
        logs.push(`❄️ Gel appliqué (${result.applyOnEnemy.gel.duration} tour${result.applyOnEnemy.gel.duration > 1 ? "s" : ""})`);
        freezeAppliedRef.current += 1;
        if (freezeAppliedRef.current >= 3) unlockAchievement?.("frozen_thrice");
      }
      if (result.applyOnEnemy.stun) logs.push(`⭐ Ennemi étourdi`);
    } else if (competence.applyOnEnemy && result.enemyStatusResisted) {
      logs.push(`🛡️ L'ennemi résiste à l'effet néfaste (RÉS ${enemy.stats.res || 0}%)`);
    } else if (competence.applyOnEnemy && result.enemyStatusLanded === false) {
      logs.push(`💨 L'effet néfaste rate (précision)`);
    }

    // Apply onSelf statuses & buffs
    if (result.applyOnSelf) {
      curPlayerEffects = mergeStatus(curPlayerEffects, result.applyOnSelf);
      if (result.applyOnSelf._buffsToApply) {
        const refStats = effectiveStats(character.stats, curPlayerEffects.combatBuffs || {});
        const curBuffs = curPlayerEffects.combatBuffs || {};
        const detailed = result.applyOnSelf._buffsToApply.map((b) => {
          let val = b.value || 0;
          if (b.fromStat) val = refStats[b.fromStat] || 0;
          if (b.doubleBns) val = curBuffs[b.doubleBns] || 0;
          return { stat: b.stat, value: val, duration: b.duration || 0 };
        });
        const buffVals = {};
        for (const d of detailed) buffVals[d.stat] = (buffVals[d.stat] || 0) + d.value;
        curPlayerEffects = applyCombatBuffs(curPlayerEffects, buffVals);
        const timed = detailed.filter((d) => d.duration > 0).map((d) => ({ stat: d.stat, value: d.value, duration: d.duration }));
        if (timed.length) curPlayerEffects = { ...curPlayerEffects, timedBuffs: [...(curPlayerEffects.timedBuffs || []), ...timed] };
        const buffsParts = detailed.map((d) => `+${d.value} ${d.stat.toUpperCase()}${d.duration ? ` (${d.duration}t)` : ""}`).join(", ");
        if (buffsParts) logs.push(`✨ Buff : ${buffsParts}`);
      }
      if (result.applyOnSelf.garde) logs.push("🛡️ Garde activée");
      if (result.applyOnSelf.posture) logs.push("🦅 Posture armée pour le prochain tour");
      if (result.applyOnSelf.provoque) logs.push("📣 Provocation appliquée");
    }

    // Heal
    if (result.healFlat) {
      updateCharacter((c) => ({ ...c, stats: { ...c.stats, pv: Math.min(c.stats.pvMax, c.stats.pv + result.healFlat) } }));
      logs.push(`💚 +${result.healFlat} PV`);
      SFX.heal();
    }
    if (result.healFormula) {
      let healAmount = 0;
      for (const term of result.healFormula) {
        healAmount += playerStats[term] || 0;
      }
      updateCharacter((c) => ({ ...c, stats: { ...c.stats, pv: Math.min(c.stats.pvMax, c.stats.pv + healAmount) } }));
      logs.push(`💚 +${healAmount} PV`);
      SFX.heal();
    }
    if (result.healPercent) {
      const healAmount = Math.round((character.stats.pvMax || 0) * result.healPercent);
      updateCharacter((c) => ({ ...c, stats: { ...c.stats, pv: Math.min(c.stats.pvMax, c.stats.pv + healAmount) } }));
      logs.push(`💚 +${healAmount} PV (${Math.round(result.healPercent * 100)}%)`);
      SFX.heal();
    }
    // Soin = % de critique BONUS infligé à l'ennemi × PV max (Oracle / Douce Brise)
    if (result.healFromEnemyCrit) {
      const enemyBonusCrit = Math.max(0, Math.round(curEnemyEffects.combatBuffs?.crit || 0));
      const healAmount = Math.round((character.stats.pvMax || 0) * enemyBonusCrit / 100);
      if (healAmount > 0) {
        updateCharacter((c) => ({ ...c, stats: { ...c.stats, pv: Math.min(c.stats.pvMax, c.stats.pv + healAmount) } }));
        logs.push(`💚 +${healAmount} PV (${enemyBonusCrit}% crit ennemi drainé)`);
        SFX.heal();
      } else {
        logs.push("💨 L'ennemi n'a aucun bonus de critique à drainer.");
      }
    }
    // Soin = bonus de stat courant (× multiplicateur), puis consommé (Libromancien / Désespoir)
    if (result.healFromBuff) {
      const amt = Math.max(0, Math.round((curPlayerEffects.combatBuffs?.[result.healFromBuff] || 0) * (result.healFromBuffMult || 1)));
      if (amt > 0) {
        updateCharacter((c) => ({ ...c, stats: { ...c.stats, pv: Math.min(c.stats.pvMax, c.stats.pv + amt) } }));
        logs.push(`💚 +${amt} PV (bonus ${result.healFromBuff.toUpperCase()} consommé)`);
        SFX.heal();
      } else {
        logs.push(`💨 Aucun bonus de ${result.healFromBuff.toUpperCase()} à consommer.`);
      }
    }

    // Consume buffs (Lumière aveuglante / Désespoir)
    if (result.consumeBuffs) {
      const newBuffs = { ...(curPlayerEffects.combatBuffs || {}) };
      for (const k of result.consumeBuffs) newBuffs[k] = 0;
      const newTimed = (curPlayerEffects.timedBuffs || []).filter((tb) => !result.consumeBuffs.includes(tb.stat));
      curPlayerEffects = { ...curPlayerEffects, combatBuffs: newBuffs, timedBuffs: newTimed };
      logs.push(`💥 Bonus ${result.consumeBuffs.join(", ").toUpperCase()} consommés`);
    }

    // Moutons
    if (result.consumeMuttons || result.consumeAllMuttons) {
      const newCount = Math.max(1, muttonsRef.current - muttonsConsumed);
      setMuttons(newCount);
      muttonsRef.current = newCount;
      if (muttonsConsumed > 0) logs.push(`🐑 -${muttonsConsumed} mouton(s)`);
      else logs.push("🐑 Dernier mouton conservé !");
      // Succès : sacrifier 5 moutons d'un coup / 100 cumulés
      if (competence.consumeAllMuttons && muttonsConsumed >= 5) unlockAchievement?.("mutton_army");
      if (muttonsConsumed > 0) bumpMeta?.("muttonsSacrificed", muttonsConsumed, { 100: "mutton_genocide" });
    }
    if (result.addMuttons) {
      const newCount = Math.min(BERGER_MUTTONS_MAX, muttonsRef.current + result.addMuttons);
      const actuallyAdded = newCount - muttonsRef.current;
      setMuttons(newCount);
      muttonsRef.current = newCount;
      logs.push(`🐑 +${actuallyAdded} mouton(s)`);
      // Succès : 9 moutons en même temps
      if (newCount >= 9) unlockAchievement?.("shepherd_full");
    }

    // Absorption VIT au kill (Noir total)
    if (result.absorbVitOnKill && curEnemyHp <= 0) {
      const vitGained = enemy.stats.vit || 0;
      curPlayerEffects = applyCombatBuffs(curPlayerEffects, { vit: vitGained });
      logs.push(`🌑 Absorbe ${vitGained} VIT !`);
    }

    // Commit state
    setEnemyHp(curEnemyHp);
    setEnemyEffects(curEnemyEffects);
    setPlayerEffects(curPlayerEffects);
    enemyHpRef.current = curEnemyHp;
    enemyEffectsRef.current = curEnemyEffects;
    playerEffectsRef.current = curPlayerEffects;

    addLogs(logs);

    setTimeout(() => endPlayerTurn(), 900);
  };

  // ============ DÉFENDRE ============
  const handleDefend = () => {
    if (!playerTurn || busy) return;
    setBusy(true);
    const before = energy;
    const newEnergy = Math.min(energyCap, energy + 2);
    setEnergy(newEnergy);
    // Si un boss charge sa signature, Défendre prépare un blocage (−80% au moment du coup)
    if (chargingRef.current) {
      chargeBlockedRef.current = true;
      addLog(`🛡️ Vous vous préparez à bloquer ${chargingRef.current.nom} !`);
    } else {
      addLog(`🔋 +${newEnergy - before} énergie`);
    }
    setTimeout(() => endPlayerTurn(), 600);
  };

  // Garde les réfs auto-combat à jour avec les derniers handlers (pattern "latest callback").
  autoSkillRef.current = handleSkill;
  autoDefendRef.current = handleDefend;
  // ============ FIN TOUR JOUEUR ============
  // Note: les effets d'état ennemis (gel/stun/brûlure) sont décrémentés à la fin du tour ennemi,
  // PAS ici — sinon un gel duration=1 disparaîtrait avant que l'ennemi ne saute son tour.
  const endPlayerTurn = () => {
    if (enemyHpRef.current <= 0) {
      handleVictory();
      return;
    }
    if (enemyHpRef.current <= 0) {
      setTimeout(() => handleVictory(), 600);
      return;
    }

    setPlayerTurn(false);
    turnNumberRef.current += 1;
    setTimeout(() => enemyTurn(), 900);
  };

  // ============ BOSS : charge signature télégraphiée ============
  // Démarre une charge : le boss annonce sa signature, sans dégâts ce tour-ci.
  const startCharge = (sig) => {
    chargingRef.current = sig;
    chargeBlockedRef.current = false;
    const threshold = Math.max(1, Math.round(enemyMaxHpRef.current * 0.10));
    setCharging({ bossNom: enemy.nom, sigNom: sig.nom, threshold });
    enemyAIRef.current.cooldowns.__sig = sig.cooldown || 4;
    addLogs([`⚠️ ${enemy.nom} CHARGE — ${sig.nom} !`, `💡 Inflige ${threshold} dégâts pour INTERROMPRE, ou DÉFENDS pour bloquer.`]);
    SFX.charge?.();
    setTimeout(() => endEnemyTurn(), 1000);
  };

  // Libère la charge (au tour suivant) si elle n'a pas été interrompue.
  const releaseCharge = () => {
    const sig = chargingRef.current;
    const blocked = chargeBlockedRef.current;
    chargingRef.current = null;
    chargeBlockedRef.current = false;
    setCharging(null);
    const playerStats = playerEffective();
    const enemyStats = enemyEffective();
    const result = computeDamageWithFormula(enemyStats, playerStats, {
      formula: ["atk"], multiplier: sig.multiplier || 2.8,
      atkElement: enemy.element, defElement: classe.element,
    });
    const logs = [`🔥 ${enemy.nom} libère ${sig.nom} !`];
    let dmg = result.hit ? result.damage : 0;
    if (blocked && dmg > 0) {
      dmg = Math.max(1, Math.round(dmg * 0.2));
      logs.push("🛡️ Charge BLOQUÉE ! Dégâts réduits de 80%.");
    }
    if (dmg > 0) {
      const newPv = Math.max(0, character.stats.pv - dmg);
      lowestPvRef.current = Math.min(lowestPvRef.current, newPv);
      showFloatingDmg(dmg, true, "player", "💥 ");
      logs.push(`💥 ${sig.nom} : ${dmg} dégâts${result.crit ? " (CRITIQUE)" : ""} !`);
      updateCharacter((c) => {
        if (!c) return c;
        const rs = { ...(c.runStats || {}) };
        rs.totalDamageTaken = (rs.totalDamageTaken || 0) + dmg;
        return { ...c, stats: { ...c.stats, pv: newPv }, runStats: rs };
      });
      combatTotalsRef.current.taken += dmg;
      setCombatTotals({ ...combatTotalsRef.current });
      SFX.bossRelease?.();
      setShakingPlayer(true);
      setTimeout(() => setShakingPlayer(false), 500);
      if (!blocked && sig.applyOnPlayer) applyEnemyEffectsOnPlayer(sig.applyOnPlayer, logs);
      addLogs(logs);
      if (newPv <= 0) {
        if (tryRevive()) {
          setTimeout(() => endEnemyTurn(), 800);
          return;
        }
        SFX.defeat();
        if (!endedRef.current) {
          endedRef.current = true;
          setTimeout(() => navigate("/gameover"), 800);
        }
        return;
      }
    } else {
      logs.push("Vous esquivez la charge !");
      SFX.miss();
      addLogs(logs);
    }
    setTimeout(() => endEnemyTurn(), 800);
  };

  // ============ TOUR ENNEMI ============
  const enemyTurn = () => {
    if (enemyHpRef.current <= 0 || character.stats.pv <= 0) return;

    // L'IA récupère ses cooldowns (même si l'ennemi est étourdi ce tour-ci)
    enemyAIRef.current = tickEnemyCooldowns(enemyAIRef.current);

    const curEnemyEffects = enemyEffectsRef.current;
    const curPlayerEffects = playerEffectsRef.current;
    let gardeBrokenVsBoss = false; // Boss qui passe la garde → attaque 100% précise

    // Si stun / gel sur ennemi → skip (et toute charge en cours fizzle)
    if (curEnemyEffects.stun?.duration > 0 || curEnemyEffects.gel?.duration > 0) {
      const label = curEnemyEffects.gel?.duration > 0 ? "❄️ gelé" : "⭐ étourdi";
      if (chargingRef.current) {
        addLog(`❄️ ${enemy.nom} perd sa concentration (${label}) — charge annulée !`);
        chargingRef.current = null;
        chargeBlockedRef.current = false;
        setCharging(null);
      } else {
        addLog(`L'ennemi est ${label} et passe son tour.`);
      }
      setTimeout(() => endEnemyTurn(), 800);
      return;
    }

    // Si une charge signature est en cours → la libérer ce tour-ci (sauf interruption déjà gérée)
    if (chargingRef.current) {
      releaseCharge();
      return;
    }

    // Si joueur a Garde → 1 chance sur 2 de bloquer (50% FIXE, indépendant de la précision de la classe)
    if (curPlayerEffects.garde?.duration > 0) {
      const garde = curPlayerEffects.garde;
      const blockSuccess = Math.random() < 0.5;
      if (!blockSuccess) {
        addLog("🛡️ La garde cède ! L'attaque passe.");
        const npe = { ...curPlayerEffects, garde: null };
        setPlayerEffects(npe);
        playerEffectsRef.current = npe;
        // → l'attaque ennemie continue normalement plus bas
      } else {
      addLog("🛡️ Vous bloquez l'attaque !");
      const playerStats = playerEffective();
      // Contre-attaque : (ATK joueur + DE ennemi)
      const counter = computeDamageWithFormula(playerStats, enemy.stats, { formula: ["atk"], atkElement: classe.element, defElement: enemy.element });
      let extraElem = 0;
      if (garde.useEnemyElem) {
        // Le DE de l'ennemi est renvoyé sur lui, réduit par sa propre résistance
        const enemyElem = enemy.stats.degatsElem || 0;
        const enemyRes = enemy.stats.res || 0;
        extraElem = Math.max(0, enemyElem * (1 - enemyRes / 100));
      }
      const bossCounterBonus = enemy.isBoss ? 1.2 : 1;
      const totalDmg = (counter.hit ? Math.round(counter.damage * bossCounterBonus) : 0) + Math.round(extraElem);
      let newEnemyHp = enemyHpRef.current;
      if (totalDmg > 0) {
        newEnemyHp = Math.max(0, newEnemyHp - totalDmg);
        showFloatingDmg(totalDmg, counter.crit, "enemy", "⚡ ");
        addLog(`⚡ Contre-attaque : ${totalDmg} dégâts (renvoi ATK + DE ennemi)`);
        setEnemyHp(newEnemyHp);
        enemyHpRef.current = newEnemyHp;
        setShakingEnemy(true);
        setTimeout(() => setShakingEnemy(false), 400);
      }
      // Counter brulure (uniquement si le contre touche ET que l'ennemi ne résiste pas)
      let newEnemyEffects = curEnemyEffects;
      if (garde.counterBrulure && counter.hit) {
        const enemyRes = enemy.stats.res || 0;
        const resisted = Math.random() * 100 < enemyRes;
        if (!resisted) {
          const dmg = Math.max(1, character.stats.degatsElem || 1);
          newEnemyEffects = mergeStatus(newEnemyEffects, { brulure: { duration: garde.counterBrulure, damage: dmg } });
          setEnemyEffects(newEnemyEffects);
          enemyEffectsRef.current = newEnemyEffects;
          addLog("🔥 L'ennemi est brûlé !");
        } else {
          addLog(`🛡️ L'ennemi résiste à la brûlure (RÉS ${enemyRes}%)`);
        }
      } else if (garde.counterBrulure && !counter.hit) {
        addLog("💨 La brûlure du contre rate (précision)");
      }
      // Consomme la garde
      const newPlayerEffects = { ...curPlayerEffects, garde: null };
      setPlayerEffects(newPlayerEffects);
      playerEffectsRef.current = newPlayerEffects;

      if (newEnemyHp <= 0) {
        setTimeout(() => handleVictory(), 600);
        return;
      }
      setTimeout(() => endEnemyTurn(), 800);
      return;
      }
    }

    // ===== BOSS : décider de démarrer une charge signature =====
    const ai0 = enemyAIRef.current;
    if (enemy.isBoss && enemy.signature && (ai0.cooldowns.__sig || 0) <= 0 && ai0.turnCount >= 2) {
      startCharge(enemy.signature);
      return;
    }

    // ===== IA maligne : choisit une compétence selon le contexte =====
    const playerStats = playerEffective();
    const enemyStats = enemyEffective();
    const ai = enemyAIRef.current;
    const ability = chooseEnemyAbility(enemy, {
      enemyHpFrac: enemyHpRef.current / Math.max(1, enemyMaxHpRef.current),
      playerHpFrac: character.stats.pv / Math.max(1, character.stats.pvMax || character.stats.pv),
      turnCount: ai.turnCount,
      cooldowns: ai.cooldowns,
      playerEffects: curPlayerEffects,
      mode: "solo",
    });

    // ----- Compétence SOIN -----
    if (ability && ability.type === "soin") {
      ai.cooldowns[ability.id] = ability.cooldown || 3;
      const healAmt = Math.round(enemyMaxHpRef.current * (ability.selfHealPercent || 0.15));
      const newHp = Math.min(enemyMaxHpRef.current, enemyHpRef.current + healAmt);
      setEnemyHp(newHp);
      enemyHpRef.current = newHp;
      SFX.heal?.();
      playAttack("enemy", "enemy", "buff");
      addLogs([`⚠️ ${enemy.nom} ${ability.telegraph || "se concentre"}…`, `💚 ${ability.nom} : +${healAmt} PV pour ${enemy.nom} !`]);
      setTimeout(() => endEnemyTurn(), 800);
      return;
    }

    // ----- Compétence DEBUFF (pas de dégâts) -----
    if (ability && ability.type === "debuff") {
      ai.cooldowns[ability.id] = ability.cooldown || 3;
      const logs = [`⚠️ ${enemy.nom} ${ability.telegraph || "prépare un sort"}…`, `✨ ${ability.nom}`];
      applyEnemyEffectsOnPlayer(ability.applyOnPlayer, logs);
      playAttack("enemy", "player", "shadow");
      addLogs(logs);
      setTimeout(() => endEnemyTurn(), 800);
      return;
    }

    // ----- Compétence ATTAQUE, ou attaque de base si aucune compétence choisie -----
    const isSkill = !!ability;
    if (isSkill) ai.cooldowns[ability.id] = ability.cooldown || 3;
    const atkLabel = isSkill ? ability.nom : "attaque";
    const logs = [];
    if (isSkill) logs.push(`⚠️ ${enemy.nom} ${ability.telegraph || "se prépare"}…`);
    const result = computeDamageWithFormula(enemyStats, playerStats, {
      formula: ["atk"],
      multiplier: isSkill ? (ability.multiplier || 1) : 1,
      atkElement: enemy.element,
      defElement: classe.element,
      forceHit: gardeBrokenVsBoss,
    });
    playAttack("enemy", "player", elementFx(enemy.element));
    if (result.hit) {
      const newPv = Math.max(0, character.stats.pv - result.damage);
      lowestPvRef.current = Math.min(lowestPvRef.current, newPv);
      showFloatingDmg(result.damage, result.crit, "player");
      logs.push(`${enemy.nom} — ${atkLabel} : ${result.damage} dégâts${result.crit ? " (CRITIQUE)" : ""}`);
      updateCharacter((c) => {
        if (!c) return c;
        const rs = { ...(c.runStats || {}) };
        rs.totalDamageTaken = (rs.totalDamageTaken || 0) + result.damage;
        return { ...c, stats: { ...c.stats, pv: newPv }, runStats: rs };
      });
      combatTotalsRef.current.taken += result.damage;
      setCombatTotals({ ...combatTotalsRef.current });
      if (result.crit) SFX.crit(); else SFX.hit();
      if (result.crit) triggerCritImpact();
      setShakingPlayer(true);
      setTimeout(() => setShakingPlayer(false), 400);
      // Effets appliqués sur le joueur si la compétence en applique et que l'attaque a touché
      if (isSkill && ability.applyOnPlayer) applyEnemyEffectsOnPlayer(ability.applyOnPlayer, logs);
      addLogs(logs);
      if (newPv <= 0) {
        if (tryRevive()) {
          setTimeout(() => endEnemyTurn(), 700);
          return;
        }
        SFX.defeat();
        if (!endedRef.current) {
          endedRef.current = true;
          setTimeout(() => navigate("/gameover"), 800);
        }
        return;
      }
    } else {
      logs.push(`Vous esquivez ${isSkill ? ability.nom : "l'attaque"} !`);
      SFX.miss();
      addLogs(logs);
    }

    setTimeout(() => endEnemyTurn(), 700);
  };

  // ============ FIN TOUR ENNEMI ============
  const endEnemyTurn = () => {
    // Met à jour l'affichage des cooldowns de l'ennemi (après son action)
    setEnemyCdView({ ...enemyAIRef.current.cooldowns });
    // Tick effects ennemis (DOT brûlure + décrémentation gel/stun) à la fin du tour de l'ennemi
    const enemyTick = tickStatusEffects(enemyEffectsRef.current);
    let newEnemyHp = enemyHpRef.current;
    if (enemyTick.damage > 0) {
      newEnemyHp = Math.max(0, newEnemyHp - enemyTick.damage);
      showFloatingDmg(enemyTick.damage, false, "enemy", "🔥 ");
      setShakingEnemy(true);
      setTimeout(() => setShakingEnemy(false), 400);
      setEnemyHp(newEnemyHp);
      enemyHpRef.current = newEnemyHp;
    }
    if (enemyTick.logs.length > 0) addLogs(enemyTick.logs);
    setEnemyEffects(enemyTick.updatedEffects);
    enemyEffectsRef.current = enemyTick.updatedEffects;
    if (newEnemyHp <= 0) {
      setTimeout(() => handleVictory(), 600);
      return;
    }

    // Tick effects joueur
    const tick = tickStatusEffects(playerEffectsRef.current);
    if (tick.damage > 0) {
      lowestPvRef.current = Math.min(lowestPvRef.current, Math.max(0, character.stats.pv - tick.damage));
      showFloatingDmg(tick.damage, false, "player", "🔥 ");
      updateCharacter((c) => ({ ...c, stats: { ...c.stats, pv: Math.max(0, c.stats.pv - tick.damage) } }));
    }
    if (tick.logs.length > 0) addLogs(tick.logs);
    setPlayerEffects(tick.updatedEffects);
    playerEffectsRef.current = tick.updatedEffects;

    if ((character.stats.pv - tick.damage) <= 0) {
      if (!tryRevive()) {
        if (!endedRef.current) {
          endedRef.current = true;
          setTimeout(() => navigate("/gameover"), 800);
        }
        return;
      }
      // Revive : on poursuit le combat (régen énergie + tour du joueur)
    }

    // Régénération énergie passive
    setEnergy((e) => Math.min(energyCap, e + 1));

    // POSTURE: auto-déclenchement si active
    if (tick.updatedEffects.posture?.duration > 0) {
      setTimeout(() => triggerAutoPosture(), 600);
      return;
    }

    setPlayerTurn(true);
    setBusy(false);
  };

  // ============ POSTURE AUTO ============
  const triggerAutoPosture = () => {
    const post = playerEffectsRef.current.posture;
    if (!post) {
      setPlayerTurn(true);
      setBusy(false);
      return;
    }
    const playerStats = playerEffective();
    const hit = computeDamageWithFormula(playerStats, enemy.stats, { formula: post.formula, atkElement: classe.element, defElement: enemy.element });
    let newEnemyHp = enemyHpRef.current;
    if (hit.hit) {
      newEnemyHp = Math.max(0, newEnemyHp - hit.damage);
      showFloatingDmg(hit.damage, hit.crit, "enemy", "🦅 ");
      setEnemyHp(newEnemyHp);
      enemyHpRef.current = newEnemyHp;
      setShakingEnemy(true);
      setTimeout(() => setShakingEnemy(false), 400);
      addLog(`🦅 Posture du Phoenix déclenchée : ${hit.damage} dégâts${hit.crit ? " (CRIT)" : ""}`);
    } else {
      addLog("🦅 Posture du Phoenix : esquive !");
    }

    // Apply stun on enemy (uniquement si la posture touche ET que l'ennemi ne résiste pas)
    let newEnemyEffects = enemyEffectsRef.current;
    if (post.stun && hit.hit) {
      const enemyRes = enemy.stats.res || 0;
      const resisted = Math.random() * 100 < enemyRes;
      if (!resisted) {
        newEnemyEffects = mergeStatus(newEnemyEffects, { stun: { duration: post.stun } });
        setEnemyEffects(newEnemyEffects);
        enemyEffectsRef.current = newEnemyEffects;
        addLog("⭐ Ennemi étourdi !");
      } else {
        addLog(`🛡️ L'ennemi résiste au stun (RÉS ${enemyRes}%)`);
      }
    } else if (post.stun && !hit.hit) {
      addLog("💨 Le stun rate (précision)");
    }

    // Clear posture
    const newPlayerEffects = { ...playerEffectsRef.current, posture: null };
    setPlayerEffects(newPlayerEffects);
    playerEffectsRef.current = newPlayerEffects;

    if (newEnemyHp <= 0) {
      setTimeout(() => handleVictory(), 600);
      return;
    }

    setTimeout(() => {
      setPlayerTurn(true);
      setBusy(false);
    }, 500);
  };

  const combatTheme = themeForFloor(floor || 1);
  // Taille des sprites selon le rang (boss > élite > normal). Position : ancrage au sol
  // (bottom) IDENTIQUE pour tous → les pieds de TOUS les ennemis sont sur la même ligne de
  // sol que le joueur (les boss/élites, plus grands, grandissent vers le HAUT, pieds alignés).
  const enemySize = enemy.isBoss
    ? "w-44 h-44 sm:w-60 sm:h-60"
    : enemy.isElite
      ? "w-36 h-36 sm:w-48 sm:h-48"
      : "w-28 h-28 sm:w-36 sm:h-36";

  // Position des combattants selon le décor. Certains biomes ont une grande arène/plateforme
  // surélevée au MILIEU (vide cosmique astral/abysse, sanctuaire céleste) → on remonte et recentre
  // les sprites pour qu'ils soient posés sur la plateforme (sinon ils semblent léviter au bord).
  // Les décors "sol bas" gardent la position par défaut. Géré par-décor (gratuit, CSS).
  const ARENA_POS = {
    astral: { player: "bottom-[26%] left-[10%] sm:left-[16%]", enemy: "bottom-[28%] right-[8%] sm:right-[13%]" },
    abyss: { player: "bottom-[26%] left-[10%] sm:left-[16%]", enemy: "bottom-[28%] right-[8%] sm:right-[13%]" },
    celestial: { player: "bottom-[24%] left-[10%] sm:left-[16%]", enemy: "bottom-[26%] right-[8%] sm:right-[14%]" },
  };
  const defaultPos = {
    player: "bottom-[14%] left-[5%] sm:left-[12%]",
    enemy: "bottom-[16%] right-[6%] sm:right-[12%]",
  };
  const pos = ARENA_POS[combatTheme.id] || defaultPos;
  const playerPos = pos.player;
  const enemyPos = pos.enemy;

  return (
    <div
      data-testid="combat-arena"
      className={`relative h-[100dvh] w-screen overflow-hidden bg-slate-900 select-none ${screenShake ? "animate-screen-shake" : ""}`}
      style={{ backgroundImage: `url(/arenas/${combatTheme.id}.png)`, backgroundSize: "cover", backgroundPosition: "center" }}
    >
      {/* Vignette + dégradé pour lisibilité par-dessus le décor */}
      <div className="absolute inset-0 z-0 pointer-events-none shadow-[inset_0_0_120px_rgba(0,0,0,0.85)] bg-gradient-to-t from-slate-950/85 via-slate-950/10 to-slate-950/40" />

      {/* Flash blanc d'impact (coups critiques / ultimes) */}
      {critFlash && <div data-testid="crit-flash" className="absolute inset-0 z-40 bg-white/30 pointer-events-none animate-crit-flash" />}

      {floatingDmg.map((f) => (
        <FloatingDamage key={f.id} {...f} onDone={() => removeFloating(f.id)} />
      ))}

      {/* ===== Contrôles haut : tour + vitesse + auto ===== */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-start justify-between gap-2 p-3 sm:p-4 pointer-events-none">
        <div className="flex items-center gap-2 pointer-events-auto">
          <span data-testid="turn-indicator" className={`font-display font-bold text-xs sm:text-sm uppercase tracking-wider px-3 py-1.5 rounded-xl border-4 border-slate-950 shadow-[3px_3px_0_0_#020617] ${playerTurn ? "bg-emerald-400 text-slate-950" : "bg-rose-500 text-white"}`}>
            {playerTurn ? "À ton tour" : "Tour ennemi"}
          </span>
          {enemy.isBoss && <span className="bg-purple-600 text-white border-4 border-slate-950 rounded-xl px-2 py-1 text-xs font-bold uppercase shadow-[3px_3px_0_0_#020617]">BOSS</span>}
          {enemy.isElite && <span className="bg-rose-700 text-white border-4 border-slate-950 rounded-xl px-2 py-1 text-xs font-bold uppercase shadow-[3px_3px_0_0_#020617]">ÉLITE</span>}
        </div>
        <div className="flex items-center gap-2 pointer-events-auto bg-slate-900/80 border-4 border-slate-950 rounded-xl p-1.5 shadow-[4px_4px_0_0_#020617] backdrop-blur-sm">
          <button data-testid="speed-toggle-button" onClick={() => setCombatSpeed((s) => (s >= 3 ? 1 : s + 1))} title="Vitesse du combat" className="font-display font-bold text-sm text-yellow-300 bg-slate-800 border-2 border-slate-950 rounded-lg px-2.5 py-1 hover:bg-slate-700 transition-colors flex items-center gap-1">
            <Gauge className="w-4 h-4" /> x{combatSpeed}
          </button>
          <button data-testid="auto-combat-button" onClick={() => setAutoMode((a) => !a)} title="Combat automatique" className={`font-display font-bold text-sm border-2 border-slate-950 rounded-lg px-2.5 py-1 transition-colors flex items-center gap-1 ${autoMode ? "bg-emerald-400 text-slate-950" : "bg-slate-800 text-slate-200 hover:bg-slate-700"}`}>
            {autoMode ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />} Auto
          </button>
        </div>
      </div>

      {victoryShown && (
        <div data-testid="victory-overlay" className="absolute inset-0 z-40 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center animate-slide-up p-4">
          <div className="bg-gradient-to-br from-yellow-400 to-amber-500 border-4 border-slate-950 rounded-3xl p-6 cartoon-shadow-lg-static text-center max-w-lg w-full">
            <Trophy className="w-16 h-16 mx-auto text-slate-950 mb-2 animate-float" />
            <h1 className="font-display text-4xl font-bold text-slate-950 mb-1" style={{ textShadow: "3px 3px 0 #fff" }}>VICTOIRE !</h1>
            <p className="text-slate-900 font-bold mb-1">{enemy.nom} vaincu</p>
            <p className="text-slate-800 font-bold mb-3">+{enemy.goldReward} or 🪙</p>
            {/* Combat recap */}
            <div data-testid="combat-recap" className="bg-slate-950/30 border-2 border-slate-950 rounded-2xl p-3 text-left text-slate-950">
              <p className="font-display font-bold uppercase text-xs tracking-wider text-center mb-2">Récap du combat</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-rose-500/90 rounded-lg px-2 py-1 flex justify-between"><span className="font-bold">⚔️ Infligés</span><span className="font-bold">{combatTotals.dealt}</span></div>
                <div className="bg-sky-500/90 rounded-lg px-2 py-1 flex justify-between"><span className="font-bold">🛡️ Subis</span><span className="font-bold">{combatTotals.taken}</span></div>
                <div className="bg-orange-500/90 rounded-lg px-2 py-1 flex justify-between"><span className="font-bold">💥 Max coup</span><span className="font-bold">{combatTotals.maxHit}</span></div>
                <div className="bg-pink-500/90 rounded-lg px-2 py-1 flex justify-between"><span className="font-bold">✨ Crits</span><span className="font-bold">{combatTotals.crits}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {charging && (
        <div data-testid="boss-charge-banner" className="absolute top-20 left-1/2 -translate-x-1/2 z-40 w-[92%] max-w-xl pointer-events-none animate-pulse">
          <div className="bg-gradient-to-r from-rose-700 via-red-600 to-rose-700 border-4 border-yellow-300 rounded-2xl px-4 py-3 cartoon-shadow-lg-static text-center">
            <p className="font-display text-lg sm:text-xl font-bold text-yellow-200 uppercase tracking-wide" style={{ textShadow: "2px 2px 0 #000" }}>
              ⚠️ {charging.bossNom} CHARGE — {charging.sigNom}
            </p>
            <p data-testid="boss-charge-hint" className="text-xs sm:text-sm text-white font-bold mt-1">
              Inflige <span className="text-yellow-300">{charging.threshold} dégâts</span> pour INTERROMPRE · ou <span className="text-sky-300">Défends</span> pour bloquer (−80%)
            </p>
          </div>
        </div>
      )}

      {/* (barre de tour déplacée dans les contrôles du haut) */}

      {/* ===== Unité ENNEMIE (droite, AU MÊME NIVEAU DE SOL que le joueur, face à lui) ===== */}
      <div ref={enemyAreaRef} className={`absolute z-10 ${enemyPos} flex flex-col items-center ${shakingEnemy ? "animate-hit-enemy" : ""}`}>
        {playerTurn && enemyHp > 0 && (
          <div data-testid="target-arrow" className="text-rose-500 text-3xl sm:text-4xl animate-bounce drop-shadow-[0_3px_0_#020617] mb-0.5">▼</div>
        )}
        <div className="flex flex-col items-center gap-1 mb-1 w-36 sm:w-44">
          <div className="flex items-center gap-1 justify-center flex-wrap">
            <span data-testid="combat-enemy-name" className="font-display text-sm sm:text-base font-bold text-white drop-shadow-[2px_2px_0_#020617]">{enemy.nom}</span>
            {enemy.element && (
              <span data-testid="enemy-element" title={ELEMENTS[enemy.element].nom} className={`${ELEMENTS[enemy.element].couleur} ${ELEMENTS[enemy.element].textColor} border-2 border-slate-950 rounded px-1 text-[10px] font-bold`}>
                {ELEMENTS[enemy.element].icon}
                {classe.element && elementMultiplier(classe.element, enemy.element) > 1 && <span className="ml-0.5">faible</span>}
                {classe.element && elementMultiplier(classe.element, enemy.element) < 1 && <span className="ml-0.5">rés.</span>}
              </span>
            )}
          </div>
          <HealthBar current={enemyHp} max={enemy.stats.pvMax} testId="combat-enemy-hp" showText={false} colorByHealth className="w-full" />
          <StatusBadges effects={enemyEffects} testId="enemy-status" />
          <EnemyAbilities competences={enemy.competences} cooldowns={enemyCdView} />
        </div>
        <div className={`relative ${enemyAttacking ? "animate-lunge-enemy" : ""}`}>
          {charging && (
            <div data-testid="boss-cast-ring" className="absolute -inset-3 z-20 pointer-events-none flex items-center justify-center">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(253,224,71,0.22)" strokeWidth="5" />
                <circle cx="50" cy="50" r="46" fill="none" stroke="#fbbf24" strokeWidth="5" strokeLinecap="round" strokeDasharray="289" strokeDashoffset="289">
                  <animate attributeName="stroke-dashoffset" from="289" to="0" dur="1.3s" repeatCount="indefinite" />
                </circle>
              </svg>
              <div className="absolute inset-1 rounded-full bg-rose-500/20 animate-ping" />
            </div>
          )}
          {/* Ombre au sol (ancrage style SW) */}
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-24 sm:w-32 h-5 bg-black/45 rounded-[100%] blur-[3px] z-0" />
          {!playerTurn && enemyHp > 0 && (
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-24 sm:w-28 h-7 bg-rose-400/40 rounded-[100%] blur-[2px] border-2 border-rose-300/70 animate-pulse" />
          )}
          <div className={`${enemySize} relative flex items-center justify-center ${enemyHp > 0 ? "" : "opacity-30 grayscale"} ${shakingEnemy ? "animate-hit-flash" : ""}`}>
            {enemySpriteUrl(enemy.id) ? (
              <EnemySprite enemyId={enemy.id} alt={enemy.nom} facingLeft={enemyFlipToFacePlayer(enemy.id)} className="w-full h-full object-contain drop-shadow-[0_10px_8px_rgba(0,0,0,0.5)]" testId="combat-enemy-sprite" />
            ) : (
              <span className="text-7xl drop-shadow-[0_6px_6px_rgba(0,0,0,0.5)]">{enemy.emoji}</span>
            )}
            {attackFx?.target === "enemy" && <AttackFX key={attackFx.id} type={attackFx.type} testId="enemy-attack-fx" />}
          </div>
        </div>
      </div>

      {/* ===== Unité JOUEUR (gauche, face à l'ennemi) ===== */}
      <div ref={playerAreaRef} className={`absolute z-20 ${playerPos} flex flex-col items-center ${shakingPlayer ? "animate-hit-player" : ""}`}>
        <div className="flex flex-col items-center gap-1 mb-1 w-36 sm:w-44">
          <div className="flex items-center gap-1 justify-center">
            <span data-testid="combat-player-name" className="font-display text-sm sm:text-base font-bold text-white drop-shadow-[2px_2px_0_#020617]">{classe.nom}</span>
            {classe.element && (
              <span data-testid="player-element" title={ELEMENTS[classe.element].nom} className={`${ELEMENTS[classe.element].couleur} ${ELEMENTS[classe.element].textColor} border-2 border-slate-950 rounded px-1 text-[10px] font-bold`}>{ELEMENTS[classe.element].icon}</span>
            )}
          </div>
          <HealthBar current={character.stats.pv} max={character.stats.pvMax} testId="combat-player-hp" showText={false} colorByHealth className="w-full" />
          <div className="flex items-center gap-0.5 flex-wrap justify-center">
            {Array.from({ length: energyCap }).map((_, i) => (
              <div key={i} data-testid={`energy-dot-${i}`} className={`w-2.5 h-2.5 rounded-full border-2 border-slate-950 ${i < energy ? "bg-sky-400" : "bg-slate-700"}`} />
            ))}
            <span className="text-[10px] ml-0.5 text-white font-bold drop-shadow-[1px_1px_0_#020617]">⚡{energy}</span>
            {classe.hasMuttons && <span data-testid="muttons-counter" className="ml-1 text-[10px] bg-yellow-700 text-white border-2 border-slate-950 rounded px-1 font-bold">🐑{muttons}</span>}
            {classe.scalesWithGold && <span data-testid="gold-power-counter" title="Bonus d'ATK selon l'or amassé" className="ml-1 text-[10px] bg-amber-500 text-slate-950 border-2 border-slate-950 rounded px-1 font-bold">🪙+{goldAtkBonus(character.runStats?.goldEarned || 0)}</span>}
            {classe.stealsGold && goldStolen > 0 && <span data-testid="gold-stolen-counter" title="Or volé ce combat" className="ml-1 text-[10px] bg-emerald-500 text-slate-950 border-2 border-slate-950 rounded px-1 font-bold animate-pulse">💰+{goldStolen}</span>}
          </div>
          <StatusBadges effects={playerEffects} testId="player-status" />
        </div>
        <div className={`relative ${playerAttacking ? "animate-lunge-player" : ""}`}>
          {/* Ombre au sol (ancrage style SW) */}
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-24 sm:w-32 h-5 bg-black/45 rounded-[100%] blur-[3px] z-0" />
          {playerTurn && character.stats.pv > 0 && (
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-24 sm:w-28 h-7 bg-yellow-400/40 rounded-[100%] blur-[2px] border-2 border-yellow-300/70 animate-pulse" />
          )}
          <div className={`w-28 h-28 sm:w-36 sm:h-36 relative ${character.stats.pv > 0 ? "" : "opacity-40 grayscale"} ${shakingPlayer ? "animate-hit-flash" : ""}`}>
            <BreathingSprite src={`/sprites/side/${character.classeId}.png`} alt={classe.nom} testId="combat-player-sprite" className="w-full h-full drop-shadow-[0_10px_8px_rgba(0,0,0,0.5)]" />
            {attackFx?.target === "player" && <AttackFX key={attackFx.id} type={attackFx.type} testId="player-attack-fx" />}
          </div>
        </div>
      </div>

      {/* ===== Barre de compétences (bas-droite, style Summoners War) ===== */}
      <div className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 z-30 flex flex-wrap gap-2 sm:gap-3 max-w-[70%] justify-end items-end pointer-events-auto">
        {classe.competences.map((comp) => {
          const isBase = comp.id === "base";
          const purchased = (character.purchasedCompetences || []).includes(comp.id);
          const unlocked = comp.purchasable ? purchased : (comp.unlockLevel || 1) <= (character.runLevel || 1);
          const muttonOk = !comp.requireMuttons || muttons >= comp.requireMuttons;
          const canCast = unlocked && energy >= comp.cout && playerTurn && !busy && muttonOk;
          const bgColor = !unlocked ? "bg-slate-600" : comp.ultimate ? "bg-gradient-to-br from-rose-500 via-orange-500 to-yellow-400" : isBase ? "bg-rose-500" : comp.type === "defense" ? "bg-blue-500" : comp.type === "buff" ? "bg-orange-500" : "bg-yellow-400";
          const textColor = (isBase || comp.type === "defense" || comp.ultimate || !unlocked) ? "text-white" : "text-slate-950";
          const lockLabel = !unlocked ? (comp.purchasable ? `💰${comp.purchaseCost}` : `Niv.${comp.unlockLevel}`) : null;
          return (
            <button
              key={comp.id}
              data-testid={`combat-skill-${comp.id}`}
              onClick={() => handleSkill(comp)}
              disabled={!canCast}
              title={unlocked ? `${comp.nom} — ${comp.description}` : (comp.purchasable ? `${comp.nom} — à acheter (${comp.purchaseCost} or)` : `${comp.nom} — verrouillé (Niv. ${comp.unlockLevel})`)}
              className={`relative ${bgColor} ${textColor} w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full border-4 border-slate-950 shadow-[3px_3px_0_0_#020617] enabled:hover:-translate-y-1 enabled:active:translate-y-0.5 enabled:active:shadow-none transition-all disabled:opacity-45 disabled:cursor-not-allowed flex flex-col items-center justify-center ${comp.ultimate && unlocked ? "animate-prismatic" : ""}`}
            >
              {!unlocked && (
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-slate-900 text-yellow-300 border-2 border-yellow-300 rounded-full px-1 text-[9px] font-bold whitespace-nowrap">{lockLabel}</span>
              )}
              <span className="text-2xl leading-none">{unlocked ? comp.icon : (comp.purchasable ? "💰" : "🔒")}</span>
              <span className="text-[8px] font-bold leading-none mt-0.5">{comp.cout === 0 ? "FREE" : `${comp.cout}⚡`}{comp.requireMuttons ? `🐑${comp.requireMuttons}` : ""}</span>
            </button>
          );
        })}
        <button data-testid="combat-defend-btn" onClick={handleDefend} disabled={!playerTurn || busy} title="Défendre (+2⚡, réduit les dégâts)"
          className="bg-blue-600 text-white w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full border-4 border-slate-950 shadow-[3px_3px_0_0_#020617] enabled:hover:-translate-y-1 transition-all disabled:opacity-45 disabled:cursor-not-allowed flex flex-col items-center justify-center">
          <Shield className="w-5 h-5" /><span className="text-[8px] font-bold mt-0.5">Défendre</span>
        </button>
        <button data-testid="combat-inventory-btn" onClick={() => setCombatInvOpen(true)} disabled={!playerTurn || busy || !(character.inventory?.length)} title="Objets"
          className="bg-amber-600 text-white w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full border-4 border-slate-950 shadow-[3px_3px_0_0_#020617] enabled:hover:-translate-y-1 transition-all disabled:opacity-45 disabled:cursor-not-allowed flex flex-col items-center justify-center">
          <Backpack className="w-5 h-5" /><span className="text-[8px] font-bold mt-0.5">{character.inventory?.length || 0} obj</span>
        </button>
      </div>

      {/* ===== Journal de combat (haut-gauche, lisible) ===== */}
      <div data-testid="combat-log" className="absolute top-16 left-2 sm:top-[4.75rem] sm:left-4 z-20 w-56 sm:w-72 bg-slate-900/85 border-4 border-slate-950 rounded-xl px-3 py-2 text-xs sm:text-sm text-slate-100 shadow-[4px_4px_0_0_#020617] backdrop-blur-sm flex flex-col justify-end gap-1 pointer-events-none">
        {log.slice(-4).map((line, i, arr) => (
          <p key={i} className={`leading-snug break-words ${i === arr.length - 1 ? "text-white font-semibold" : "text-slate-300"}`}>{line}</p>
        ))}
      </div>

      <InventoryModal open={combatInvOpen} onClose={() => setCombatInvOpen(false)} inCombat={true} onUseInCombat={handleUseInCombat} />
    </div>
  );
};

export default Combat;
